"use client";

import React from "react";
import { Terminal, Download, Play, Key, Monitor, ShieldCheck, Lock, EyeOff, Video, ChevronRight, MonitorPlay, Apple, MonitorSmartphone, CircleCheck, ServerCrash, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const processSteps = [{
  id: "step-1",
  title: "Download the Helper Tool",
  description: "Download the helper tool to establish a secure remote connection with the technician.",
  icon: Download
}, {
  id: "step-2",
  title: "Run the Tool",
  description: "Just click the installer and follow the simple on-screen steps to launch the helper tool.",
  icon: Play
}, {
  id: "step-3",
  title: "Share Your Code",
  description: "Share your unique 9-digit session code with the technician so they can securely connect to your computer.",
  icon: Key
}, {
  id: "step-4",
  title: "Connect & Help",
  description: "The technician connects and helps you resolve your issue with full visibility and control on your side.",
  icon: MonitorPlay
}];
const reassurances = [{
  id: "re-1",
  title: "Your Data is Protected",
  description: "All data transmitted is secured with AES-256 encryption.",
  icon: Lock
}, {
  id: "re-2",
  title: "We Don't Stay on Your Computer",
  description: "The client runs in memory and leaves no trace after closing.",
  icon: EyeOff
}, {
  id: "re-3",
  title: "You Stay in Control",
  description: "I cannot connect without you actively providing the session ID.",
  icon: ShieldCheck
}, {
  id: "re-4",
  title: "Everything is Recorded",
  description: "All actions are logged and can be recorded for your review.",
  icon: Video
}];
const osInstructions = [{
  id: "os-win",
  os: "Windows",
  icon: MonitorSmartphone,
  steps: ["Click the download button.", "Open the downloaded file.", "If asked, allow it to run.", "Give the 9-digit code to the technician."]
}, {
  id: "os-mac",
  os: "macOS",
  icon: Apple,
  steps: ["Click the download button.", "Open the downloaded file.", "If asked, allow it to run.", "Give the 9-digit code to the technician."]
}];
const faqs = [{
  id: "faq-1",
  question: "Can you spy on me after we're done?",
  answer: "No – the connection ends as soon as we finish. Once you close the app, we lose all access."
}, {
  id: "faq-2",
  question: "Is the download safe?",
  answer: "Yes. It is a secure tool used only for this repair session. Your antivirus might ask for permission, which is normal."
}, {
  id: "faq-3",
  question: "What if my internet cuts out?",
  answer: "The session will stop immediately. You'll just need to open the tool again and give us a new code to continue."
}];
export default function RemoteSupportHelp() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: processRef,
    isVisible: isProcessVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll();
  const {
    ref: osRef,
    isVisible: isOsVisible
  } = useRevealOnScroll();
  const {
    ref: faqRef,
    isVisible: isFaqVisible
  } = useRevealOnScroll();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll();
  return <section id="remotesupporthelp" className="relative min-h-screen overflow-hidden bg-background py-24 text-foreground">
      <div className="pointer-events-none absolute inset-0 z-0 opacity-5">
        <div className="absolute inset-0" style={{
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, var(--primary) 2px, var(--primary) 4px)",
        backgroundSize: "100% 4px"
      }} />
      </div>
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--background)_100%)]" />

      <div className="container relative z-10 mx-auto max-w-6xl px-4 sm:px-6 md:px-8 lg:px-12">
        <div ref={headerRef} className={`mb-12 sm:mb-16 md:mb-20 text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-primary/30 bg-primary/10">
              <Terminal className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="mb-4 font-default text-3xl font-bold tracking-tight text-primary sm:text-4xl md:text-5xl lg:text-6xl">
            Need Help? Remote Support Made Easy
            <span className="animate-[blink-cursor_1s_infinite] inline-block h-8 w-4 bg-primary align-middle md:h-12 md:w-6"></span>
          </h2>
          <p className="mx-auto max-w-2xl font-mono text-sm text-muted-foreground sm:text-base md:text-lg">I can help you fix your computer from home.</p>
        </div>

        <div ref={processRef} className={`mb-16 sm:mb-24 md:mb-32 transition-all duration-1000 delay-200 ${isProcessVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="grid gap-4 sm:gap-6 md:gap-8 lg:gap-10 md:grid-cols-4">
            {processSteps.map((step, i) => {
            const Icon = step.icon;
            return <div key={step.id} data-index={i} className="relative">
                  <Card className="relative h-full border-primary/20 bg-card/50 backdrop-blur-sm transition-colors hover:border-primary/50">
                    <CardHeader className="pb-4">
                      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="font-default text-base text-foreground sm:text-lg md:text-xl">
                        <span className="mr-2 text-primary">0{i + 1}.</span>
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-mono text-xs text-muted-foreground sm:text-sm md:text-base">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                  {i < processSteps.length - 1 && <div className="absolute top-1/2 -right-5 hidden -translate-y-1/2 text-primary/30 md:block">
                      <ChevronRight className="h-8 w-8" />
                    </div>}
                </div>;
          })}
          </div>
        </div>

        <div ref={gridRef} className={`mb-16 sm:mb-24 md:mb-32 transition-all duration-1000 delay-300 ${isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-8 flex items-center gap-4">
            <Separator className="flex-1 bg-primary/20" />
            <Badge variant="outline" className="border-primary/30 font-mono text-primary">
              Your Safety First
            </Badge>
            <Separator className="flex-1 bg-primary/20" />
          </div>
          <div className="grid gap-4 sm:gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reassurances.map((item, i) => {
            const Icon = item.icon;
            return <div key={item.id} data-index={i} className="flex items-start gap-4 rounded-lg border border-border/50 bg-card/30 p-4">
                  <div className="mt-1 shrink-0 text-secondary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="mb-1 font-default text-sm font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="font-mono text-xs text-muted-foreground sm:text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          <div ref={osRef} className={`transition-all duration-1000 delay-400 ${isOsVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}>
            <h3 className="mb-6 flex items-center gap-2 font-default text-xl font-semibold text-foreground sm:text-2xl md:text-3xl">
              <Terminal className="h-6 w-6 text-primary" />
              How to Get Started
            </h3>
            <Accordion type="single" collapsible className="w-full" defaultValue="os-win">
              {osInstructions.map((os, i) => {
              const Icon = os.icon;
              return <AccordionItem key={os.id} value={os.id} data-index={i} className="border-primary/20">
                    <AccordionTrigger className="font-mono text-lg hover:text-primary hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        {os.os} Environment
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="rounded-md bg-black/40 p-4 font-mono text-sm text-muted-foreground sm:text-base">
                        <ul className="space-y-3 sm:space-y-4">
                          {os.steps.map((step, stepIdx) => <li key={stepIdx} className="flex items-start gap-3 sm:gap-4">
                              <span className="mt-0.5 text-primary">&gt;</span>
                              <span>{step}</span>
                            </li>)}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>

          <div ref={faqRef} className={`transition-all duration-1000 delay-500 ${isFaqVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}>
            <h3 className="mb-6 flex items-center gap-2 font-default text-xl font-semibold text-foreground sm:text-2xl md:text-3xl">
              <ServerCrash className="h-6 w-6 text-secondary" />
              Common Questions
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-primary/20">
                  <AccordionTrigger className="text-left font-mono text-sm hover:text-secondary hover:no-underline sm:text-base">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-mono text-xs text-muted-foreground sm:text-sm">
                    <div className="border-l-2 border-secondary/50 pl-4">
                      {faq.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        <div ref={ctaRef} className={`mt-16 sm:mt-24 md:mt-32 text-center transition-all duration-1000 delay-600 ${isCtaVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mx-auto max-w-md rounded-xl border border-primary/30 bg-card/40 p-8 backdrop-blur-md">
            <Cpu className="mx-auto mb-6 h-12 w-12 text-primary opacity-80" />
            <h3 className="mb-2 font-default text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
              Ready to Get Help?
            </h3>
            <p className="mb-4 font-mono text-sm text-muted-foreground sm:text-base md:text-lg">
              Click below to download the helper tool. It's safe and takes 30 seconds.
            </p>
            <p className="mb-8 font-mono text-xs text-muted-foreground/80 italic sm:text-sm">I use AnyDesk, a trusted industry-standard tool, to securely connect to your system.</p>
            <Button size="lg" className="w-full animate-[pulse-glow_3s_infinite] bg-primary font-mono text-primary-foreground hover:bg-primary/90" asChild>
              <Link to="#">
                <Download className="mr-2 h-5 w-5" />
                Download Helper Tool
              </Link>
            </Button>
            <p className="mt-4 font-mono text-xs text-muted-foreground/60 sm:text-sm">
              v2.4.1 | 14.2 MB | Secure Connection
            </p>
          </div>
        </div>
      </div>
    </section>;
}